import { NgCheckbox, NgInput } from "@/atoms";
import { IconChevronRight, IconSearch } from "@tabler/icons-react";
import * as React from "react";
import { useEffect } from "react";

import { cn } from "@oneleet/utils";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";

const itemClassNames =
  "text-ng-md mx-1.5 cursor-pointer select-none rounded px-3 py-2 text-neutral-950 outline-none transition-colors duration-300 focus:bg-neutral-100 focus:duration-0";

const contentClassNames =
  "py-1.5 min-w-48 rounded-lg bg-white shadow-[0px_4px_6px_-4px_#11101008,0px_10px_15px_0px_#1110100A,0px_0px_0px_1px_#23222213] overflow-auto max-h-[90vh] z-[100]";

// Fix: Clear pointer-events when selecting a dropdown item to prevent app unresponsiveness
// when opening modals.
// Upstream issue: https://github.com/radix-ui/primitives/issues/3317
const clearBodyPointerEvents = () => {
  if (typeof document !== "undefined" && document.body.style.pointerEvents) {
    document.body.style.removeProperty("pointer-events");
  }
};

interface DropdownMenuContextValue {
  inputRef: React.RefObject<HTMLInputElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** Focus the first focusable dropdown menu item inside the content */
  focusFirstItem: () => void;
  /** Focus the input element */
  focusInput: () => void;
}

const DropdownMenuContext =
  React.createContext<DropdownMenuContextValue | null>(null);

const useDropdownMenuContext = () => {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error(
      "NgDropdownMenu components must be used within a NgDropdownMenuProvider",
    );
  }
  return context;
};

const NgDropdownMenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  const focusFirstItem = () => {
    if (contentRef.current) {
      // Look for the first element marked as a dropdown item.
      const firstItem = contentRef.current.querySelector(
        "[data-dropdown-item]",
      );
      if (firstItem && firstItem instanceof HTMLElement) {
        firstItem.focus();
      }
    }
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <DropdownMenuContext.Provider
      value={{ inputRef, contentRef, focusFirstItem, focusInput }}
    >
      {children}
    </DropdownMenuContext.Provider>
  );
};

const NgDropdownMenuRoot: React.FC<
  React.ComponentProps<typeof DropdownMenuPrimitive.Root>
> = (props) => {
  return (
    <NgDropdownMenuProvider>
      <DropdownMenuPrimitive.Root {...props} />
    </NgDropdownMenuProvider>
  );
};

/**
 * The DropdownMenuSub component is a wrapper around the Radix DropdownMenuSub component.
 * It provides a more convenient API for using the DropdownMenuSub component.
 * Check the Storybook for more examples.
 *
 * @example
 * ```jsx
 * <DropdownMenuSub>
 *   <DropdownMenuSubTrigger>Convert</DropdownMenuSubTrigger>
 *   <DropdownMenuSubContent>
 *     <DropdownMenuItem>Convert to PDF</DropdownMenuItem>
 *     <DropdownMenuItem>Convert to DOCX</DropdownMenuItem>
 *   </DropdownMenuSubContent>
 * </DropdownMenuSub>
 * ```
 */
const NgDropdownMenuSub = DropdownMenuPrimitive.Sub;

const NgDropdownMenuTrigger = ({
  asChild,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) => (
  <DropdownMenuPrimitive.Trigger
    asChild={asChild ?? true}
    className={cn("group outline-none", className)}
    ref={ref}
    {...props}
  />
);
NgDropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const NgDropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      itemClassNames,
      "radix-state-open:bg-neutral-100 flex items-center gap-2",
      inset && "pl-8",
      className,
    )}
    {...props}
  >
    {children}
    <IconChevronRight className="text-neutral-550 ml-auto size-4" />
  </DropdownMenuPrimitive.SubTrigger>
);
NgDropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const NgDropdownMenuSubContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn(
        contentClassNames,
        "-mt-1",
        "radix-state-open:animate-popoverIn radix-state-closed:animate-popoverOut",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
);
NgDropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const NgDropdownMenuContent = ({
  className,
  sideOffset = 4,
  alignOffset = 0,
  ref: forwardedRef,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) => {
  const { contentRef, focusInput } = useDropdownMenuContext();

  useEffect(() => {
    return () => {
      // Ensure pointer-events is restored when content unmounts.
      // Fixes a bug where the app becomes no longer responsive/interactive when clicking outside of both a dropdown menu and a modal at the same time.
      setTimeout(() => {
        clearBodyPointerEvents();
      }, 0);
    };
  }, []);

  // This prevents the dropdown menu from propagating the event upwards when the input loses focus.
  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    // Since it also handles the focus of non-dropdown items, any other key press besides ArrowDown
    // should also move focus to the input.
    if (e.key !== "ArrowDown") {
      focusInput();
    }
  };

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={(node) => {
          contentRef.current = node;
          if (typeof forwardedRef === "function") {
            forwardedRef(node);
          } else if (forwardedRef) {
            forwardedRef.current = node;
          }
        }}
        onKeyDown={handleKeyDown}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={cn(
          contentClassNames,
          "radix-state-open:animate-popoverIn radix-state-closed:animate-popoverOut",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
};
NgDropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const NgDropdownMenuItem = ({
  disabled,
  inset,
  className,
  keepOpenOnSelect,
  ref,
  onKeyDown,
  onSelect,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  keepOpenOnSelect?: boolean;
}) => {
  const { inputRef, contentRef, focusInput } = useDropdownMenuContext();

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (inputRef.current !== null) {
      e.stopPropagation();
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
        focusInput();
      }

      // Radix likes to use space to select a hovered item, but we don't want that when there is an input.
      if (e.key === " ") {
        e.preventDefault();
        inputRef.current.value += " ";
      }

      // If the user presses ArrowUp on the first item, move focus to the input.
      if (e.key === "ArrowUp" && contentRef.current) {
        const items = contentRef.current.querySelectorAll(
          "[data-dropdown-item]",
        );
        if (items.length > 0 && items[0] === e.currentTarget) {
          e.preventDefault();
          focusInput();
        }
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        itemClassNames,
        disabled && "cursor-default opacity-50",
        inset && "pl-8",
        className,
      )}
      disabled={disabled}
      data-dropdown-item
      onKeyDown={handleKeyDown}
      onSelect={(e) => {
        if (keepOpenOnSelect) {
          e.preventDefault();
        }
        onSelect?.(e);
        clearBodyPointerEvents();
      }}
      {...props}
    />
  );
};
NgDropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const NgDropdownMenuLabel = ({
  className,
  inset,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "text-ng-xs text-neutral-550 font-ng-medium px-4 py-1 uppercase",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
);
NgDropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const NgDropdownMenuSeparator = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("my-1.5 h-px bg-neutral-200", className)}
    {...props}
  />
);
NgDropdownMenuSeparator.displayName =
  DropdownMenuPrimitive.Separator.displayName;

const NgDropdownMenuCheckboxItem = ({
  children,
  className,
  inset,
  keepOpenOnSelect,
  onSelect,
  ref,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem> & {
  inset?: boolean;
  keepOpenOnSelect?: boolean;
}) => {
  const { inputRef, contentRef, focusInput } = useDropdownMenuContext();

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (inputRef.current !== null) {
      e.stopPropagation();
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
        focusInput();
      }

      // Radix likes to use space to select a hovered item, but we don't want that when there is an input.
      if (e.key === " ") {
        e.preventDefault();
        inputRef.current.value += " ";
      }

      // If the user presses ArrowUp on the first item, move focus to the input.
      if (e.key === "ArrowUp" && contentRef.current) {
        const items = contentRef.current.querySelectorAll(
          "[data-dropdown-item]",
        );
        if (items.length > 0 && items[0] === e.currentTarget) {
          e.preventDefault();
          focusInput();
        }
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      data-dropdown-item
      className={cn(
        itemClassNames,
        inset && "pl-8",
        "flex items-center gap-2",
        className,
      )}
      onSelect={(e) => {
        if (keepOpenOnSelect) {
          e.preventDefault();
        }
        onSelect?.(e);
        clearBodyPointerEvents();
      }}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <NgCheckbox checked={props.checked} />
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
};
NgDropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const NgDropdownMenuInput = ({
  variant,
  className,
  ...props
}: React.ComponentProps<typeof NgInput>) => (
  <NgInput
    variant={variant ?? "plain"}
    className={cn("ml-1.5 pb-[1px] pt-0.5", className)}
    {...props}
  />
);
NgDropdownMenuInput.displayName = "NgDropdownMenuInput";

const NgDropdownMenuInputIcon = ({
  icon,
  ...props
}: Partial<React.ComponentProps<typeof NgInput.Icon>>) => (
  <NgInput.Icon icon={icon ?? <IconSearch />} {...props} />
);
NgDropdownMenuInputIcon.displayName = "NgDropdownMenuInputIcon";

const NgDropdownMenuInputInput = ({
  onKeyDown,
  ref: forwardedRef,
  children,
  className,
  ...props
}: React.ComponentProps<typeof NgInput.Input>) => {
  const { inputRef, focusFirstItem } = useDropdownMenuContext();

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    // When pressing ArrowDown in the input, move focus to the first dropdown item.
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusFirstItem();
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <NgInput.Input
      {...props}
      className={className}
      onKeyDown={handleKeyDown}
      autoFocus
      ref={(node) => {
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) {
          forwardedRef.current = node;
        }
        if (node) {
          inputRef.current = node;
        }
      }}
    />
  );
};
NgDropdownMenuInputInput.displayName = "NgDropdownMenuInputInput";

/**
 * The DropdownMenu component is a wrapper around the Radix DropdownMenu component.
 * It provides a more convenient API for using the DropdownMenu component.
 *
 * @example
 * ```jsx
 * <DropdownMenu>
 *   <DropdownMenuTrigger>
 *     <Button>Open Dropdown</Button>
 *   </DropdownMenuTrigger>
 *   <DropdownMenuContent>
 *     <DropdownMenuLabel>Selected frameworks</DropdownMenuLabel>
 *     <DropdownMenuSeparator />
 *     <DropdownMenuItem>React</DropdownMenuItem>
 *     <DropdownMenuItem>Angular</DropdownMenuItem>
 *     <DropdownMenuItem>Vue</DropdownMenuItem>
 *     <DropdownMenuItem>Svelte</DropdownMenuItem>
 *     <DropdownMenuItem>Solid</DropdownMenuItem>
 *   </DropdownMenuContent>
 * </DropdownMenu>
 * ```
 */
const NgDropdownMenu = Object.assign(NgDropdownMenuRoot, {
  Trigger: NgDropdownMenuTrigger,
  Content: NgDropdownMenuContent,
  Item: NgDropdownMenuItem,
  Label: NgDropdownMenuLabel,
  Separator: NgDropdownMenuSeparator,
  Sub: NgDropdownMenuSub,
  SubTrigger: NgDropdownMenuSubTrigger,
  SubContent: NgDropdownMenuSubContent,
  CheckboxItem: NgDropdownMenuCheckboxItem,
  Input: Object.assign(NgDropdownMenuInput, {
    Input: NgDropdownMenuInputInput,
    Icon: NgDropdownMenuInputIcon,
  }),
});

export default NgDropdownMenu;
